// Pickups: item stacks lying in the world (a drop from a broken crate, a defeated enemy's loot), collected by walking
// over them into an inventory. Plain data on sim time the game keeps in its sim and its save. A drop scatters its
// stacks round where it happened on the game's seeded stream (the game gives the ground's height), and a pickup can
// be collected only after a short delay (the pop is seen first). What doesn't fit the inventory stays on the ground.
import type { Gathered } from "./gather";
import type { Inventory, InventoryState } from "./inventory";

/** One stack on the ground. */
export interface Pickup {
  id: number;
  item: string;
  count: number;
  /** Where it rests. */
  pos: [number, number, number];
  /** Where it popped from (the view arcs it from here to `pos`). */
  from: [number, number, number];
  /** Sim time it appeared (s). */
  born: number;
}

/** Every pickup in the world, and the next id. */
export interface PickupState {
  next: number;
  list: Pickup[];
}

/** A new, empty state. */
export const emptyPickups = (): PickupState => ({ next: 1, list: [] });

/** How pickups scatter and are collected (the game's values). */
export interface PickupRules {
  /** Collected within this distance of the collector's feet, across the ground (m). */
  radius: number;
  /** Collectable this long after it appeared (s). */
  delay: number;
  /** Stacks land between `spread[0]` and `spread[1]` metres from the drop's point. */
  spread: [number, number];
}

/** One collection. */
export interface Collected {
  id: number;
  item: string;
  count: number;
  /** The whole pickup went (false: the inventory took part of it). */
  gone: boolean;
}

/** Scatter a drop's stacks round `at` (each on the ground under its spot), on the seeded stream. Returns the new
 *  pickups. */
export function spawnPickups(s: PickupState, drops: readonly Gathered[], at: [number, number, number], now: number, rules: PickupRules, random: () => number, groundAt: (x: number, z: number) => number): Pickup[] {
  const out: Pickup[] = [];
  const n = drops.length;
  const turn = random() * Math.PI * 2;
  drops.forEach((d, i) => {
    const a = turn + (i / Math.max(1, n)) * Math.PI * 2 + (random() - 0.5) * 0.6;
    const r = rules.spread[0] + random() * (rules.spread[1] - rules.spread[0]);
    const x = at[0] + Math.sin(a) * r;
    const z = at[2] + Math.cos(a) * r;
    const p: Pickup = { id: s.next++, item: d.item, count: d.count, pos: [x, groundAt(x, z), z], from: [...at], born: now };
    s.list.push(p);
    out.push(p);
  });
  return out;
}

/** Collect every pickup in reach of `feet` that is old enough, into the inventory (what doesn't fit stays). */
export function collectPickups(s: PickupState, feet: [number, number, number], now: number, rules: PickupRules, inv: Inventory, invState: InventoryState): Collected[] {
  const out: Collected[] = [];
  for (const p of s.list) {
    if (now - p.born < rules.delay - 1e-9) continue;
    if (Math.hypot(p.pos[0] - feet[0], p.pos[2] - feet[2]) > rules.radius) continue;
    if (Math.abs(p.pos[1] - feet[1]) > 2) continue;
    const left = inv.add(invState, p.item, p.count);
    const took = p.count - left;
    if (took === 0) continue;
    p.count = left;
    out.push({ id: p.id, item: p.item, count: took, gone: left === 0 });
  }
  s.list = s.list.filter((p) => p.count > 0);
  return out;
}
